// Currency conversion + formatting helpers used across the app.
//
// These rates are static fallbacks. A real FX feed (exchangerate.host /
// Open Exchange Rates) would replace this map; rates here roughly match
// 2026 averages and are kept in one place so we update them once.
export const CURRENCY_RATES_VS_USD = {
  USD: 1,
  INR: 1 / 83.3,
  EUR: 1.09,
  GBP: 1.27,
  CAD: 0.74,
  AUD: 0.65,
  JPY: 1 / 151.0,
  HKD: 0.128,
  AED: 0.272,
  SGD: 0.74,
};

/**
 * Convert an amount from one currency to another using USD as the bridge.
 * Unknown currency codes pass through as 1:1 to USD (better than crashing).
 */
export const convertAmount = (amount, from, to) => {
  const fromCode = (from || "USD").toUpperCase();
  const toCode = (to || "USD").toUpperCase();
  if (fromCode === toCode) return amount || 0;
  const fromRate = CURRENCY_RATES_VS_USD[fromCode] ?? 1;
  const toRate = CURRENCY_RATES_VS_USD[toCode] ?? 1;
  const usd = (amount || 0) * fromRate;
  return usd / toRate;
};

/**
 * Sum a list of holdings (each with marketValue + currency) into a single
 * display currency. Always use this instead of summary.totalValue when the
 * user might hold positions in multiple currencies.
 */
export const totalInCurrency = (holdings, displayCurrency) =>
  (holdings || []).reduce(
    (sum, h) =>
      sum +
      convertAmount(h?.marketValue || 0, h?.currency || "USD", displayCurrency),
    0,
  );

/**
 * Sum a single field (e.g. costBasis, unrealizedPL) across holdings into
 * a single display currency.
 */
export const sumFieldInCurrency = (holdings, field, displayCurrency) =>
  (holdings || []).reduce(
    (sum, h) =>
      sum +
      convertAmount(h?.[field] || 0, h?.currency || "USD", displayCurrency),
    0,
  );

/**
 * Format a number as a currency string. Uses Intl.NumberFormat so it picks
 * the right symbol, separator, and grouping (e.g. ₹1,00,000 vs $100,000).
 */
export const formatCurrency = (amount, currency = "USD", opts = {}) => {
  const code = (currency || "USD").toUpperCase();
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: code,
      maximumFractionDigits: opts.maxFractionDigits ?? 2,
      minimumFractionDigits: opts.minFractionDigits ?? 2,
    }).format(amount || 0);
  } catch {
    // Unknown / unsupported currency code — fall back to a plain number with the code prefix.
    return `${code} ${(amount || 0).toLocaleString(undefined, {
      maximumFractionDigits: 2,
    })}`;
  }
};

/**
 * Pick the currency to render values in: the user's saved display_currency
 * (from UserProfile), or USD as a sensible default.
 */
export const resolveDisplayCurrency = (profile) =>
  (profile?.display_currency || "USD").toUpperCase();
